import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/layout/Navbar';
import { Card, Button, Input, Spinner } from '../components/ui';
import { paymentApi, userApi } from '../services/api';
import { toast } from 'sonner';

export default function PaymentPage() {
  const { t, i18n } = useTranslation();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('click');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');

  const quickAmounts = [10000, 25000, 50000, 100000];

  const paymentMethods = [
    { id: 'click', label: 'CLICK', icon: 'touch_app', color: 'bg-blue-500/10 text-blue-500' },
    { id: 'payme', label: 'PAYME', icon: 'credit_card', color: 'bg-emerald-500/10 text-emerald-500' },
    { id: 'uzcard', label: "UZCARD", icon: 'credit_score', color: 'bg-red-500/10 text-red-500' },
    { id: 'cash', label: t("payment.methods.cash"), icon: 'payments', color: 'bg-amber-500/10 text-amber-500' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [u, txs] = await Promise.all([
        userApi.getProfile(),
        paymentApi.getTransactions()
      ]);
      setProfile(u);
      setTransactions(txs);
    } catch (err) {
      console.error("Fetch data error:", err);
    } finally {
      setDataLoading(false);
    }
  };

  const handlePay = async () => {
    if (!amount || Number(amount) < 1000) {
      setError(t("payment.errors.minAmount"));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await paymentApi.topup({ amount: Number(amount), providerRef: method.toUpperCase() });
      toast.success(t("payment.messages.success"));
      setAmount('');
      await fetchData();
      window.dispatchEvent(new CustomEvent('smartgaz:balance-updated'));
    } catch (err) {
      setError(err.message || t("apiErrors.INTERNAL_ERROR"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto py-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-1.5 w-8 bg-[#1e9f6b] rounded-full" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#1e9f6b] italic">FINANCE</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white italic uppercase tracking-tighter">{t("payment.title")}</h1>
          <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 opacity-70">{t("payment.subtitle")}</p>
        </motion.div>

        {/* Balance Card */}
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="mb-10">
          <Card className="p-10 bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] border-0 rounded-[40px] shadow-2xl shadow-[#1e9f6b]/30 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-60" />
            <div className="relative z-10">
              <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mb-4 italic">{t("payment.currentBalance")}</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-6xl font-black text-white italic tracking-tighter">
                  {profile?.balance?.toLocaleString() || '0'}
                </span>
                <span className="text-white/60 text-lg font-black italic uppercase tracking-widest">{t("common.uzs")}</span>
              </div>
              <div className="flex items-center gap-3 py-3 px-5 bg-white/10 backdrop-blur-md rounded-2xl w-fit border border-white/10">
                <span className={`material-symbols-outlined text-[18px] ${Number(profile?.balance) >= 0 ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {Number(profile?.balance) >= 0 ? 'check_circle' : 'warning'}
                </span>
                <span className="text-white text-[10px] font-black uppercase tracking-widest italic">
                  {Number(profile?.balance) >= 0 ? t("payment.status.noDebt") : t("payment.status.hasDebt")}
                </span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Payment Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 italic">{t("payment.refillTitle")}</h3>
            
            <div className="space-y-8">
              {/* Amount Input */}
              <div className="space-y-4">
                <Input
                  label={t("payment.amountLabel")}
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  icon="payments"
                  className="h-16 text-xl font-black italic tracking-tight rounded-2xl"
                  error={error}
                />
                <div className="grid grid-cols-4 gap-3">
                  {quickAmounts.map((val) => (
                    <button
                      key={val}
                      onClick={() => setAmount(val.toString())}
                      className="h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#1e9f6b] hover:border-[#1e9f6b]/30 hover:bg-[#1e9f6b]/5 transition-all active:scale-95"
                    >
                      +{val / 1000}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Methods */}
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic ml-1">{t("payment.methodLabel")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {paymentMethods.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMethod(m.id)}
                      className={`h-20 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 transition-all duration-300 ${
                        method === m.id 
                          ? 'border-[#1e9f6b] bg-[#1e9f6b]/5' 
                          : 'border-slate-50 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-200'
                      }`}
                    >
                      <span className={`material-symbols-outlined text-2xl ${method === m.id ? 'text-[#1e9f6b]' : 'text-slate-400'}`}>{m.icon}</span>
                      <span className={`text-[9px] font-black uppercase tracking-[0.2em] italic ${method === m.id ? 'text-[#1e9f6b]' : 'text-slate-500'}`}>{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handlePay} 
                loading={loading} 
                size="xl" 
                className="h-16 w-full font-black uppercase tracking-[0.2em] text-sm shadow-2xl shadow-[#1e9f6b]/20"
              >
                {t("payment.payNow")}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Transaction History */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5">
            <div className="px-8 py-8 border-b border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/30 dark:bg-slate-800/20">
              <div className="space-y-1">
                <h2 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.2em] italic">{t("payment.historyTitle")}</h2>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{transactions.length} {t("payment.transactions")}</p>
              </div>
              {dataLoading && <Spinner size="sm" />}
            </div>
            
            {!dataLoading && transactions.length === 0 && (
                <div className="py-24 text-center text-slate-300 dark:text-slate-700 flex flex-col items-center gap-4">
                    <span className="material-symbols-outlined text-[64px] opacity-20">history</span>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] italic">{t("payment.historyEmpty")}</p>
                </div>
            )}

            <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
              {transactions.map((tx, idx) => (
                <motion.div
                  key={tx.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + idx * 0.05 }}
                  className="flex items-center justify-between px-8 py-6 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all duration-300"
                >
                  <div className="flex items-center gap-5">
                    <div className={`h-12 w-12 rounded-[18px] flex items-center justify-center shadow-lg transition-transform hover:scale-105 ${
                        tx.type === 'TOPUP' ? 'bg-emerald-500/10 text-emerald-500 shadow-emerald-500/5' : 'bg-red-500/10 text-red-500 shadow-red-500/5'
                    }`}>
                      <span className="material-symbols-outlined text-[20px] fill-1">
                        {tx.type === 'TOPUP' ? 'arrow_downward' : 'arrow_upward'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-black italic uppercase tracking-tight text-slate-900 dark:text-white">
                          {tx.type === 'TOPUP' ? t("payment.types.topup") : t("payment.types.payment")}
                      </p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                          {new Date(tx.createdAt).toLocaleString(i18n.language === 'uz_latin' ? 'uz-UZ' : 'ru-RU')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-black italic uppercase tracking-tighter ${tx.type === 'TOPUP' ? 'text-[#1e9f6b]' : 'text-red-500'}`}>
                      {tx.type === 'TOPUP' ? '+' : '-'}{Number(tx.amount).toLocaleString()}
                    </p>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mt-0.5">{tx.reason || t("common.user")}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
}

